import { useCallback, useEffect, useState } from 'react';
import { useAction } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loader } from '@/components/ui/Loader';
import { ProvisioningTable } from './ProvisioningTable';

type Route = { id: number; name: string; def_dest?: string; def_dest_value?: string };
type Trunk = { id: number; name: string };
type Ivr = { id: number; name: string; number: string };

interface RoutesTabProps {
  customerId: string;
}

export function RoutesTab({ customerId }: RoutesTabProps) {
  const { canManage } = useModuleAccess('routes');

  const routesList = useAction(backendApi.provisioning.routesList);
  const trunksList = useAction(backendApi.provisioning.trunksList);
  const ivrsList = useAction(backendApi.provisioning.ivrsList);
  const routesSet = useAction(backendApi.provisioning.routesSet);

  const [routes, setRoutes] = useState<Route[]>([]);
  const [trunks, setTrunks] = useState<Trunk[]>([]);
  const [ivrs, setIvrs] = useState<Ivr[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', trunkIds: [] as number[], ivrId: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t, i] = await Promise.all([
        routesList({ customerId, source: 'ui' }),
        trunksList({ customerId, source: 'ui' }),
        ivrsList({ customerId, source: 'ui' }),
      ]);
      setRoutes(r);
      setTrunks(t);
      setIvrs(i);
    } catch (error) {
      setRoutes([]);
      setTrunks([]);
      setIvrs([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load routes');
    } finally {
      setLoading(false);
    }
  }, [customerId, ivrsList, routesList, trunksList]);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const existing = routes.find((r) => r.name === form.name);
    try {
      await routesSet({
        customerId,
        source: 'ui',
        name: form.name,
        trunkIds: form.trunkIds,
        defDest: 'ivr',
        defDestValue: form.ivrId,
        existingId: existing?.id,
      });
      toast.success('Route saved');
      setShowForm(false);
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Save failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Inbound Routes</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{routes.length} routes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <Button size="sm" onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-1" />
              Create / Update
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
        Time-based routing (Workday / Holiday) cannot be set via API. Configure time conditions in
        the PBX web UI.
      </div>

      {canManage && showForm && (
        <Card animate={false}>
          <form onSubmit={handleSave} className="space-y-4">
            <Input
              label="Route name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Trunks
              </label>
              <div className="flex flex-wrap gap-3">
                {trunks.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.trunkIds.includes(t.id)}
                      onChange={(e) => {
                        setForm({
                          ...form,
                          trunkIds: e.target.checked
                            ? [...form.trunkIds, t.id]
                            : form.trunkIds.filter((x) => x !== t.id),
                        });
                      }}
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                    {t.name}
                  </label>
                ))}
                {trunks.length === 0 && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">No trunks available</span>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Destination IVR
              </label>
              <select
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={form.ivrId}
                onChange={(e) => setForm({ ...form, ivrId: e.target.value })}
                required
              >
                <option value="">Select IVR</option>
                {ivrs.map((i) => (
                  <option key={i.id} value={String(i.id)}>
                    {i.name} ({i.number})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <Button type="submit">Save route</Button>
              <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card animate={false} className="p-0 overflow-hidden">
        {loading ? (
          <Loader text="Loading routes..." />
        ) : (
          <ProvisioningTable
            columns={[
              { key: 'name', label: 'Name' },
              { key: 'dest', label: 'Default dest' },
              { key: 'destValue', label: 'Dest value' },
            ]}
            rows={routes.map((r) => ({
              name: r.name,
              dest: r.def_dest ?? '—',
              destValue: r.def_dest_value ?? '—',
            }))}
            emptyMessage="No inbound routes configured."
          />
        )}
      </Card>
    </div>
  );
}